import type { ReactNode } from "react";
import { AppSidebar, SIDEBAR_WIDTH } from "@/components/waggle/AppSidebar";
import { cn } from "@/lib/utils";

/**
 * Sidebar + main column for signed-in, non-wizard pages (Home, Settings). The /create/* wizard keeps its own full-width layout because its
 * chat rail and condensed-layout hooks assume the whole viewport.
 */
export function PageShell({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <AppSidebar />
      <main
        className={cn("flex flex-col gap-6 px-4 py-6 sm:px-6 lg:py-8 lg:pr-8 lg:pl-[calc(var(--sidebar-w)+2rem)]", className)}
        style={{ "--sidebar-w": SIDEBAR_WIDTH } as React.CSSProperties}
      >
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ title, description, action }: { title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-base text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** The soft-tinted round icon badge used on empty states and dialogs. */
export function IconBadge({ children, tone = "brand", className }: { children: ReactNode; tone?: "brand" | "muted" | "success"; className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full [&_svg]:h-6 [&_svg]:w-6",
        tone === "brand" && "bg-brand-100 text-brand-600 ring-8 ring-brand-50",
        tone === "muted" && "bg-muted text-muted-foreground ring-8 ring-muted/50",
        tone === "success" && "bg-green-100 text-green-700 ring-8 ring-green-50",
        className,
      )}
    >
      {children}
    </div>
  );
}
